import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import axios from "axios";

// -------- helpers --------
function prettifyStatus(s) {
  switch (s) {
    case "success":
    case "manual_approved":
      return { label: "Ödendi", color: "success" };
    case "pending":
    case "manual_waiting":
      return { label: "Beklemede", color: "info" };
    case "failed":
    case "manual_rejected":
      return { label: "Hata", color: "error" };
    case "refunded":
      return { label: "İade", color: "warning" };
    case "cancelled":
      return { label: "İptal", color: "default" };
    default:
      return { label: s, color: "default" };
  }
}

function formatDate(dt) {
  if (!dt) return "—";
  const d = new Date(dt);
  return d.toLocaleString("tr-TR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// -------- component --------
export default function PaymentsListPage() {
  // table data
  const [allPayments, setAllPayments] = useState([]);
  const [loadingTable, setLoadingTable] = useState(true);

  // search filter
  const [search, setSearch] = useState("");

  // manual payment dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [savingManual, setSavingManual] = useState(false);

  // manual payment form state
  const [formUserId, setFormUserId] = useState("");
  const [formPackageId, setFormPackageId] = useState("");
  const [formAmount, setFormAmount] = useState("");
  const [formReceiptNumber, setFormReceiptNumber] = useState("");
  const [formProofFileUrl, setFormProofFileUrl] = useState("");

  const [formCampaignId, setFormCampaignId] = useState("");
  const [formPaidAt, setFormPaidAt] = useState("");
  const [allCampaignOptions, setAllCampaignOptions] = useState([]);
  const [campaignOptions, setCampaignOptions] = useState([]);

  // dropdown data
  const [userOptions, setUserOptions] = useState([]);
  const [packageOptions, setPackageOptions] = useState([]);

  // detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);

  // toast/snackbar
  const [toastOpen, setToastOpen] = useState(false);
  const [toastType, setToastType] = useState("success");
  const [toastMsg, setToastMsg] = useState("");

  // ---------- load data on mount ----------
  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoadingTable(true);

        // 1) get payments
        const payRes = await axios.get(
          "/api/admin/payments"
        );

        // normalize for table rows
        const rows = payRes.data.map((p) => ({
          id: p._id,
          userName: p.userName || "—",
          packageName: p.packageName || "—",
          amount: p.amount,
          status: p.status,
          method: p.method,
          paymentChannel: p.paymentChannel,
          createdAt: p.createdAt
        }));

        setAllPayments(rows);

        // 2) load users for manual payment dialog
        const usersRes = await axios.get(
          "/api/users"
        );
        const uOpts = usersRes.data
          .filter((u) => u.role !== "admin") // adminleri çıkar
          .map((u) => ({
            id: u._id,
            label: `${u.name || ""} ${u.surname || ""}`.trim() || "Unnamed",
            email: u.email,
          }));

        setUserOptions(uOpts);

        // 3) load packages for manual payment dialog
        const pkgRes = await axios.get(
          "/api/packages"
        );

        const pOpts = pkgRes.data.data.map((pkg) => ({
          id: pkg._id,
          label: pkg.name,
          price: pkg.price,
        }));
        setPackageOptions(pOpts);
        // 3) load campaigns for manual payment dialog
        const campRes = await axios.get(
          "/api/campaigns"
        );
        console.log("loaded campaigns:", campRes.data.data);
        // kampanya modelinin alanlarını varsayıyorum: _id, campaignName, campaignCode, discountAmount, discountRate
        const cOpts = campRes.data.data.map(c => ({
          id: c._id,
          label: c.name || c.description || "Kampanya",
          code: c.code,
          discountAmount: c.discountAmount,
          discountRate: c.discountRate,
          applicablePackages: c.packages || [],
        }));
        setAllCampaignOptions(cOpts);
        setCampaignOptions([]);

      } catch (err) {
        console.error("payments page load error:", err);
        setToastType("error");
        setToastMsg("Ödemeler yüklenemedi.");
        setToastOpen(true);
      } finally {
        setLoadingTable(false);
      }
    };

    fetchAll();
  }, []);

  // ---------- search filtering ----------
  const filteredPayments = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allPayments;
    return allPayments.filter((row) => {
      return (
        row.userName?.toLowerCase().includes(q) ||
        row.userEmail?.toLowerCase().includes(q) ||
        row.packageName?.toLowerCase().includes(q) ||
        row.status?.toLowerCase().includes(q) ||
        row.method?.toLowerCase().includes(q) ||
        row.paymentChannel?.toLowerCase().includes(q)
      );
    });
  }, [allPayments, search]);

  // ---------- open/close manual dialog ----------
  const handleOpenManual = () => setDialogOpen(true);
  const handleCloseManual = () => {
    if (!savingManual) setDialogOpen(false);
  };

  // ---------- save manual payment ----------
  const handleSaveManualPayment = async () => {
    if (!formUserId || !formAmount) {
      setToastType("error");
      setToastMsg("Kullanıcı ve tutar zorunlu.");
      setToastOpen(true);
      return;
    }

    try {
      setSavingManual(true);

      let campaignPayload;
      if (formCampaignId) {
        const camp = campaignOptions.find(c => c.id === formCampaignId);
        if (camp) {
          campaignPayload = {
            campaignId: camp.id,
            campaignName: camp.label,
            campaignCode: camp.code,
            discountRate: camp.discountRate ?? null,
            discountAmount: camp.discountAmount ?? null,
          };
        }
      }

      const payload = {
        userId: formUserId,
        packageId: formPackageId || undefined,
        amount: Number(formAmount),
        currency: "TRY",
        refCode: formReceiptNumber || undefined,
        proofFileUrl: formProofFileUrl || undefined,
        paidAt: formPaidAt ? new Date(formPaidAt).toISOString() : undefined,
        campaign: campaignPayload,
      };

      const res = await axios.post(
        "/api/admin/payments/manual",
        payload
      );

      // optimistic add
      const newRow = {
        id: res.data._id,
        userName:
          userOptions.find((u) => u.id === formUserId)?.label || "—",
        userEmail:
          userOptions.find((u) => u.id === formUserId)?.email || "",
        packageName:
          packageOptions.find((p) => p.id === formPackageId)?.label || "—",
        amount: res.data.amount,
        status: res.data.status,
        method: res.data.method,
        paymentChannel: res.data.paymentChannel,
        createdAt: res.data.createdAt,
        paidAt: res.data.paidAt,
      };

      setAllPayments((prev) => [newRow, ...prev]);

      // cleanup
      setFormUserId("");
      setFormPackageId("");
      setFormCampaignId("");
      setFormPaidAt("");
      setFormAmount("");
      setFormReceiptNumber("");
      setFormProofFileUrl("");

      setDialogOpen(false);

      setToastType("success");
      setToastMsg("Manuel ödeme oluşturuldu.");
      setToastOpen(true);
    } catch (err) {
      console.error("manual payment save error:", err);
      setToastType("error");
      setToastMsg("Manuel ödeme kaydedilemedi.");
      setToastOpen(true);
    } finally {
      setSavingManual(false);
    }
  };

  // ---------- open detail modal ----------
  const handleOpenDetail = async (id) => {
    try {
      setDetailLoading(true);
      setDetailOpen(true);

      const res = await axios.get(
        `/api/admin/payments/${id}`
      );

      // we want raw Payment doc, nicely formatted for UI
      const p = res.data;
      setDetailData({
        id: p._id,
        userName: p.userName || "—",
        packageName: p.packageName || "—",
        packagePrice: p.packageId?.price,
        amount: p.amount,
        currency: p.currency,
        status: p.status,
        method: p.method,
        paymentChannel: p.paymentChannel,
        receiptNumber: p.receiptNumber,
        description: p.description,
        proofFileUrl: p.proofFileUrl,
        refCode: p.refCode,
        transactionId: p.transactionId,
        isApproved: p.isApproved,
        paidAt: p.paidAt,
        createdAt: p.createdAt,
        errorMessage: p.errorMessage,
        userAgent: p.userAgent,
        campaign: p.campaign,
        events: p.events,
      });
    } catch (err) {
      setDetailData({
        error: "Detay yüklenemedi.",
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setDetailData(null);
  };

  // ---------- table columns ----------
  const columns = useMemo(
    () => [
      {
        field: "userName",
        headerName: "Kullanıcı",
        flex: 1.2,
        renderCell: (params) => (
          <Box height={"100%"} display="flex" alignItems="center">
            <Typography fontWeight={600} variant="body2">
              {params.row.userName}
            </Typography>
          </Box>
        ),
      },
      {
        field: "packageName",
        headerName: "Paket",
        flex: 1,
        renderCell: (params) => (
          <Box height={"100%"} display="flex" alignItems="center">
            <Typography variant="body2">{params.row.packageName}</Typography>
          </Box>
        ),
      },
      {
        field: "amount",
        headerName: "Tutar",
        flex: 0.6,
        renderCell: (params) => (
          <Box height={"100%"} display="flex" alignItems="center">
            <Typography fontWeight={600}>
              ₺{Number(params.row.amount || 0).toLocaleString("tr-TR")}
            </Typography>
          </Box>
        ),
      },
      {
        field: "status",
        headerName: "Durum",
        flex: 0.7,
        renderCell: (params) => {
          const s = prettifyStatus(params.row.status);
          return (
            <Chip
              size="small"
              label={s.label}
              color={s.color}
              variant="outlined"
            />
          );
        },
      },
      {
        field: "method",
        headerName: "Yöntem",
        flex: 0.6,
        renderCell: (params) => (
          <Box height={"100%"} display="flex" alignItems="center">
            <Typography variant="body2" fontWeight={500}>
              {params.row.method?.toUpperCase() || "-"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "createdAt",
        headerName: "Oluşturulma",
        flex: 0.9,
        renderCell: (params) => (
          <Box height={"100%"} display="flex" alignItems="center">
            <Typography variant="body2">{formatDate(params.row.createdAt)}</Typography>
          </Box>
        ),
      },
      {
        field: "actions",
        headerName: "İşlemler ",
        flex: 0.6,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <IconButton
            size="small"
            onClick={() => handleOpenDetail(params.row.id)}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    []
  );

  return (
    <Box p={3}>
      {/* header row */}
      <Box mb={3} display="flex" alignItems="center" justifyContent="space-between" width={"97%"}>
        <TextField
          label="Kullanıcı / paket ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: "30%" }}
          size="small"
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenManual}
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
        >
          Manuel Ödeme Ekle
        </Button>
      </Box>


      {/* table */}
      <Box
        sx={{
          width: "100%",
          "& .MuiDataGrid-root": {
            borderRadius: 2,
          },
        }}
      >
        {loadingTable ? (
          <Box
            py={8}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box mb={2} mt={2} sx={{ width: "97%" }}>
            <DataGrid
              rows={filteredPayments}
              columns={columns}
              getRowId={(row) => row.id}
              pageSize={10}
              autoHeight
              disableRowSelectionOnClick
            />
          </Box>
        )}
      </Box>

      {/* MANUAL PAYMENT DIALOG */}
      <Dialog open={dialogOpen} onClose={handleCloseManual} fullWidth maxWidth="sm">
        <DialogTitle>Manuel Ödeme Ekle</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} mt={0}>
            {/* USER */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel id="user-label">Kullanıcı</InputLabel>
                <Select
                  labelId="user-label"
                  label="Kullanıcı"
                  value={formUserId}
                  onChange={(e) => setFormUserId(e.target.value)}
                >
                  {userOptions.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.label} ({u.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* PACKAGE (optional) */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel id="package-label">Paket</InputLabel>
                <Select
                  labelId="package-label"
                  label="Paket"
                  value={formPackageId}
                  onChange={(e) => {
                    const newPackageId = e.target.value;
                    setFormPackageId(newPackageId);

                    // kampanya seçimini resetle
                    setFormCampaignId("");
                    console.log("selected package:", newPackageId);
                    if (!newPackageId) {
                      // paket temizlendiyse kampanya listesi boşalsın
                      setCampaignOptions([]);
                      return;
                    }

                    // client-side filtre: bu pakette geçerli kampanyalar
                    const filtered = allCampaignOptions.filter((camp) =>
                      camp.applicablePackages?.some((p) => p._id === newPackageId)
                    );
                    setCampaignOptions(filtered);
                  }}
                >
                  <MenuItem value="">
                    <em>Seçme</em>
                  </MenuItem>
                  {packageOptions.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.label} – ₺{p.price}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* CAMPAIGN (optional) */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel id="campaign-label">Kampanya</InputLabel>
                <Select
                  labelId="campaign-label"
                  label="Kampanya"
                  value={formCampaignId}
                  onChange={(e) => setFormCampaignId(e.target.value)}
                  disabled={!formPackageId || campaignOptions.length === 0}
                >
                  <MenuItem value="">
                    <em>Kampanya Yok</em>
                  </MenuItem>

                  {campaignOptions.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.label}
                      {c.code ? ` (${c.code})` : ""}{" "}
                      {c.discountAmount
                        ? ` -₺${Number(c.discountAmount).toLocaleString("tr-TR")}`
                        : c.discountRate
                          ? ` -%${c.discountRate}`
                          : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* AMOUNT */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Tutar (₺)"
                size="small"
                type="number"
                value={formAmount}
                onChange={(e) => setFormAmount(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            {/* PAID AT */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Ödeme Tarihi"
                size="small"
                type="datetime-local"
                value={formPaidAt}
                onChange={(e) => setFormPaidAt(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {/* RECEIPT NUMBER */}
            <Grid item xs={12}>
              <TextField
                label="Makbuz No / Dekont No"
                size="small"
                value={formReceiptNumber}
                onChange={(e) => setFormReceiptNumber(e.target.value)}
                fullWidth
              />
            </Grid>

            {/* PROOF URL */}
            <Grid item xs={12}>
              <TextField
                label="Dekont URL (opsiyonel)"
                size="small"
                value={formProofFileUrl}
                onChange={(e) => setFormProofFileUrl(e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseManual} disabled={savingManual}>
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveManualPayment}
            disabled={savingManual}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            {savingManual ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DETAIL DIALOG */}
      <Dialog open={detailOpen} onClose={handleCloseDetail} fullWidth maxWidth="sm">
        <DialogTitle>Ödeme Detayı</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box py={4} display="flex" alignItems="center" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : detailData?.error ? (
            <Typography color="error">{detailData.error}</Typography>
          ) : detailData ? (
            <Box display="grid" rowGap={1.5}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Kullanıcı
                </Typography>
                <Typography fontWeight={600}>
                  {detailData.userName}
                </Typography>
              </Box>

              <Divider />

              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Paket
                  </Typography>
                  <Typography fontWeight={600}>
                    {detailData.packageName || "—"}
                  </Typography>
                  {detailData.packagePrice != null && (
                    <Typography variant="caption" color="text.secondary">
                      ₺{Number(detailData.packagePrice).toLocaleString("tr-TR")}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Tutar
                  </Typography>
                  <Typography fontWeight={600}>
                    ₺{Number(detailData.amount || 0).toLocaleString("tr-TR")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {detailData.currency || "TRY"}
                  </Typography>
                </Box>
              </Box>

              <Divider />

              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Durum
                  </Typography>
                  {(() => {
                    const s = prettifyStatus(detailData.status);
                    return (
                      <Chip
                        size="small"
                        label={s.label}
                        color={s.color}
                        variant="outlined"
                      />
                    );
                  })()}
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Yöntem / Kanal
                  </Typography>
                  <Typography fontWeight={500}>
                    {detailData.method?.toUpperCase() || "-"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {detailData.paymentChannel || "-"}
                  </Typography>
                </Box>
              </Box>

              <Divider />

              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Oluşturulma
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(detailData.createdAt)}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Ödeme Alındı
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(detailData.paidAt)}
                  </Typography>
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Makbuz / Dekont No
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {detailData.paymentChannel === "admin"
                    ? detailData.refCode || "—"
                    : "Web işleminde makbuz bilgisi bulunmamaktadır."}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {detailData.description || ""}
                </Typography>
                {detailData.proofFileUrl && (
                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{ wordBreak: "break-all" }}
                  >
                    {detailData.proofFileUrl}
                  </Typography>
                )}
              </Box>

              <Divider />

              {detailData.campaign && (
                <>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Kampanya
                    </Typography>

                    <Typography variant="body2" fontWeight={600}>
                      {detailData.campaign.campaignName || "—"}
                    </Typography>

                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                      Kod: {detailData.campaign.campaignCode || "—"}
                    </Typography>

                    <Typography variant="body2">
                      İndirim Tutarı:{" "}
                      {detailData.campaign.discountAmount != null
                        ? `₺${Number(
                          detailData.campaign.discountAmount
                        ).toLocaleString("tr-TR")}`
                        : "—"}
                    </Typography>

                    <Typography variant="body2">
                      İndirim Oranı:{" "}
                      {detailData.campaign.discountRate != null
                        ? `%${detailData.campaign.discountRate}`
                        : "—"}
                    </Typography>
                  </Box>

                  <Divider />
                </>
              )}

              {detailData.errorMessage && (
                <>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Hata Mesajı
                    </Typography>
                    <Typography variant="body2" color="error">
                      {detailData.errorMessage}
                    </Typography>
                  </Box>
                  <Divider />
                </>
              )}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Teknik Bilgi
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                  Ref: {detailData.refCode || "—"}
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                  TxID: {detailData.transactionId || "—"}
                </Typography>
              </Box>

              {Array.isArray(detailData.events) && detailData.events.length > 0 && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Event Log
                    </Typography>
                    {detailData.events.map((ev, idx) => (
                      <Box key={idx} mb={1}>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}
                        >
                          {ev.type
                            ? `${ev.type}: ${ev.message || ""}`
                            : typeof ev === "string"
                              ? ev
                              : JSON.stringify(ev)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Veri yok.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetail}>Kapat</Button>
        </DialogActions>
      </Dialog>

      {/* TOAST */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toastType}
          onClose={() => setToastOpen(false)}
          sx={{ width: "100%" }}
        >
          {toastMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
